import React, { useRef, useEffect, useState } from "react";
import * as nouislider from "nouislider";
import "nouislider/dist/nouislider.css";
import { RenderingEngine } from "@cornerstonejs/core";

interface WindowLevelSliderProps {
  windowWidth: number;
  windowLevel: number;
  onWindowChange: (wl: number, ww: number) => void;
}

const WindowLevelSlider: React.FC<WindowLevelSliderProps> = ({
  windowWidth,
  windowLevel,
  onWindowChange,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const lastScanRef = useRef<number>(Date.now());
  const [initialized, setInitialized] = useState<boolean>(false);

  // Convert window level/width to slider values
  const convertWLWWtoSliderVal = (wl: number, ww: number): [number, number] => {
    const wwHalf = ww / 2;
    return [wl - wwHalf, wl + wwHalf];
  };

  // Convert slider values to window level/width
  const convertSliderValToWLWW = (
    sl1: number,
    sl2: number
  ): [number, number] => {
    // Ensure sl1 <= sl2
    const min = Math.min(sl1, sl2);
    const max = Math.max(sl1, sl2);

    const wl = (min + max) / 2;
    const ww = max - min;
    return [wl, ww];
  };

  // Initialize the slider
  useEffect(
    () => {
      // Only run once on mount
      if (!sliderRef.current || initialized) return;

      const sliderValues = convertWLWWtoSliderVal(windowLevel, windowWidth);

      try {
        nouislider.create(sliderRef.current, {
          orientation: "vertical",
          start: sliderValues,
          connect: [true, true, true],
          behaviour: "drag",
          // CT window ranges: good starting points for different tissues
          // Bone: WL=500 WW=2000, Soft Tissue: WL=40 WW=400, Lung: WL=-600 WW=1500
          range: { min: -1500, max: 1500 },
          step: 1,
          margin: 1,
        });

        // Set the colors for the connect elements
        const connect = sliderRef.current.querySelectorAll(".noUi-connect");
        if (connect.length >= 3) {
          (connect[0] as HTMLElement).style.background = "rgb(255, 255, 255)";
          (connect[1] as HTMLElement).style.background = "rgb(127, 127, 127)";
          (connect[2] as HTMLElement).style.background = "rgb(0, 0, 0)";
        }

        // Event for when user starts dragging
        (sliderRef.current as any).noUiSlider.on("start", () => {
          isUserDraggingRef.current = true;
        });

        // Event for during slide with throttling
        (sliderRef.current as any).noUiSlider.on("slide", () => {
          if (Date.now() - lastScanRef.current < 200) {
            return;
          }
          lastScanRef.current = Date.now();

          const slider = (sliderRef.current as any)?.noUiSlider?.get();
          if (slider && Array.isArray(slider)) {
            const [wl, ww] = convertSliderValToWLWW(
              parseFloat(slider[1] as string),
              parseFloat(slider[0] as string)
            );

            // Update our stored values
            prevWLRef.current = wl;
            prevWWRef.current = ww;

            onWindowChange(wl, ww);
          }
        });

        // Event for when user stops dragging
        (sliderRef.current as any).noUiSlider.on("end", () => {
          // Get final values
          const slider = (sliderRef.current as any)?.noUiSlider?.get();
          if (slider && Array.isArray(slider)) {
            const [wl, ww] = convertSliderValToWLWW(
              parseFloat(slider[1] as string),
              parseFloat(slider[0] as string)
            );

            // Update our stored values
            prevWLRef.current = wl;
            prevWWRef.current = ww;

            // Send one final update
            onWindowChange(wl, ww);
          }

          // Reset the flag after a short delay to allow for the update to propagate
          setTimeout(() => {
            isUserDraggingRef.current = false;
          }, 300);
        });

        setInitialized(true);
      } catch (error) {
        console.error("Error initializing slider:", error);
      }

      // Only clean up when component unmounts
      return () => {
        if (sliderRef.current && (sliderRef.current as any).noUiSlider) {
          try {
            (sliderRef.current as any).noUiSlider.destroy();
          } catch (error) {
            console.error("Error destroying slider:", error);
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      /* empty dependency array to run only once on mount */
    ]
  );

  // Store previous values to prevent unnecessary updates
  const prevWLRef = useRef(windowLevel);
  const prevWWRef = useRef(windowWidth);

  // Flag to track if changes are coming from the user or external sources
  const isUserDraggingRef = useRef(false);

  // Update slider only when props change from external sources, not from user interaction
  useEffect(() => {
    if (!initialized || !(sliderRef.current as any)?.noUiSlider) return;

    // Don't update if user is dragging
    if (isUserDraggingRef.current) {
      return;
    }

    // Only update if coming from outside
    try {
      const sliderValues = convertWLWWtoSliderVal(windowLevel, windowWidth);
      (sliderRef.current as any).noUiSlider.set(sliderValues);

      // Update stored values
      prevWLRef.current = windowLevel;
      prevWWRef.current = windowWidth;
    } catch (error) {
      console.error("Error updating slider:", error);
    }
  }, [windowLevel, windowWidth, initialized]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: 500,
      }}
    >
      <div style={{ height: "100%" }} ref={sliderRef}></div>
      <div
        style={{
          marginTop: "16px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "12px",
        }}
      >
        {/* Preset Buttons */}
        <img
          onClick={() => onWindowChange(500, 2000)}
          src="/images/bone.png"
          alt="Bone"
          title="Bone preset (WL: 500, WW: 2000)"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
        />
        <img
          onClick={() => onWindowChange(50, 350)}
          src="/images/stissue.png"
          alt="SoftTissue"
          title="Soft tissue preset (WL: 50, WW: 350)"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
        />
        <img
          onClick={() => onWindowChange(-600, 1600)}
          src="/images/lungs.png"
          alt="Lungs"
          title="Lungs preset (WL: -600, WW: 1600)"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
        />
        <img
          onClick={() => onWindowChange(35, 70)}
          src="/images/brain.png"
          alt="Brain"
          title="Brain preset (WL: 35, WW: 70)"
          style={{ cursor: "pointer", width: "24px", height: "24px" }}
        />
      </div>
    </div>
  );
};

export default WindowLevelSlider;
